import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as PopoverUpdateOnChange,
} from '@atlaskit/spotlight';
import { FadeIn } from '@atlaskit/motion';
import Heading from '@atlaskit/heading';
import Portal from '@atlaskit/portal';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	AIStreamingSpotlightTitle: {
		id: 'onboarding-ai-first-create.ai-streaming.spotlight.title',
		defaultMessage: 'View changes or undo',
		description: 'Title for AI streaming spotlight',
	},
	AIStreamingSpotlightDescription: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.description',
		defaultMessage: 'Compare your edits and Rovo’s to a prior version and undo if needed.',
		description: 'Description text for AI streaming spotlight',
	},
	AIStreamingSpotlightCTA: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.cta',
		defaultMessage: 'OK',
		description: 'CTA button text for AI streaming spotlight',
	},
});

export type AIStreamingSpotlightProps = {
	children: React.ReactNode;
	aiIsStreaming: boolean;
	primaryActionsLength: number;
};

export const AIStreamingSpotlight = ({
	children,
	aiIsStreaming,
	primaryActionsLength,
}: AIStreamingSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const prevIsAIStreamingRef = useRef(aiIsStreaming);
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const { setOnboardingState } = useSetOnboardingState();

	const { data, loading, error } = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { hasSeenAIStreamingSpotlight } = deserializeState(data);

	useEffect(() => {
		if (aiIsStreaming) {
			prevIsAIStreamingRef.current = aiIsStreaming;
		}
	}, [aiIsStreaming]);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIStreamingSpotlight,
			value: 'true',
		});

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AIStreamingSpotlight',
				attributes: {
					source: 'onboarding-ai-first-create',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const { useChoreographer } = useChoreographerAssets();

	const aiStreamingSpotlightChoreographer = useChoreographer('AIStreamingSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleSpotlightInteraction = useCallback(
		(action: string, actionSubject: string, actionSubjectIdSuffix?: string) => {
			setIsSpotlightActive(false);

			aiStreamingSpotlightChoreographer.stopMessage();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: `AIStreamingSpotlight${actionSubjectIdSuffix ?? ''}`,
					source: 'onboarding-ai-first-create',
				},
			}).fire();
		},
		[createAnalyticsEvent, aiStreamingSpotlightChoreographer],
	);

	const handleOnClick = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'CTA');
	}, [handleSpotlightInteraction]);

	const handleOnDismiss = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'Dismiss');
	}, [handleSpotlightInteraction]);

	useEffect(() => {
		if (
			!loading &&
			!error &&
			!hasSeenAIStreamingSpotlight &&
			!aiIsStreaming &&
			prevIsAIStreamingRef.current &&
			primaryActionsLength !== 0 &&
			fg('aifc_onboarding_killswitch')
		) {
			aiStreamingSpotlightChoreographer.startMessage();
		}
	}, [
		loading,
		error,
		hasSeenAIStreamingSpotlight,
		aiIsStreaming,
		primaryActionsLength,
		aiStreamingSpotlightChoreographer,
	]);

	if (!isSpotlightActive) {
		return <>{children}</>;
	}

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange />
			<PopoverTarget>
				<Flex direction="column" gap="space.100">
					{children}
				</Flex>
			</PopoverTarget>
			<Portal zIndex={600}>
				<PopoverContent
					dismiss={handleOnDismiss}
					isVisible={isSpotlightActive}
					placement="left-start"
					offset={[40, 0]}
				>
					<FadeIn entranceDirection="left">
						{(props) => (
							<div {...props}>
								<SpotlightCard testId="ai-streaming-spotlight">
									<SpotlightHeader>
										<SpotlightHeadline>
											<Heading as="span" size="small">
												{formatMessage(i18n.AIStreamingSpotlightTitle)}
											</Heading>
										</SpotlightHeadline>
										<SpotlightControls>
											<SpotlightDismissControl />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightBody>
										<Text>{formatMessage(i18n.AIStreamingSpotlightDescription)}</Text>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightPrimaryAction onClick={handleOnClick}>
												{formatMessage(i18n.AIStreamingSpotlightCTA)}
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							</div>
						)}
					</FadeIn>
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	);
};
